import {
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import FilteredTable from "../../components/FilteredTable";
import { LoadingMask } from "../../components/LoadingMask";
import { Modal } from "../../components/Modal";
import { Toast } from "../../components/Toast";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormRadioButtonSelect } from "../../components/form/ReactiveFormRadioButtonSelect";
import { RoleEnum } from "../../enums/Role";
import { NewTagData, Tag } from "../../models/Tags";
import { AppDispatch } from "../../state/store";
import { createTag, deleteTag, updateTag } from "../../state/tags/action";
import { User } from "../../models/User";
import { AgencyTagModal } from "../../components/agency/tag/AgencyTagModal";
import { AgencyDeleteTagModal } from "../../components/agency/tag/AgencyDeleteTagModal";

export const Tags = () => {
  const { t } = useTranslation();
  const tags = useSelector((state: any) => state.tags.tags);
  const isLoading = useSelector((state: any) => state.tags.isLoading);
  const error = useSelector((state: any) => state.tags.error);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: any) => state.users.currentUser);

  const [showAgencyTagModal, setShowAgencyTagModal] = useState<{
    show: boolean;
    update: boolean;
    tag?: Tag;
    currentUser: User;
  }>({ show: false, update: false, tag: undefined, currentUser: currentUser });

  const [ showDeleteTagModal, setShowDeleteTagModal] = useState<{
    tag?: Tag;
    show: boolean;
  }>({ show: false, tag: undefined });

  useEffect(() => {
    if (showError) {
      Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
      setShowError(false);
    }
  }, [showError, error, t]);

  const columns = useMemo(
    () => [
      {
        Header: t("LABEL"),
        accessor: "label",
      },
      {
        Header: t("CREATED_BY"),
        accessor: "user.name",
      },
      {
        Header: t("TAG_TYPE"),
        accessor: "tag_type",
      },
      {
        Header: " ",
        Cell: (row: any) => {
          return (
            <td className="flex flex-row justify-center divide-x">
              <Button
                isTerciary
                isDisabled={row.row.original.user.id !== currentUser.id}
                iconOnly
                tooltip={t("UPDATE_TAG")}
                onClick={() => setShowAgencyTagModal({
                  show: true,
                  update: true,
                  tag: row.row.original,
                  currentUser: currentUser
                })}
                classNames="mr-2"
              >
                <PencilIcon className="h-5 aspect-square text-blue" />
              </Button>
              <Button
                isTerciary
                isDisabled={row.row.original.user.id !== currentUser.id}
                tooltip={t("DELETE_TAG")}
                iconOnly
                onClick={() => setShowDeleteTagModal({ show: true, tag: row.row.original })}
                classNames="pl-2"
              >
                <TrashIcon className="h-5 aspect-square text-red-600" />
              </Button>
            </td>
          );
        },
      },
    ],
    [t, currentUser]
  );

  const onDeleteSubmit = async (tag: Tag) => {
    dispatch(
      deleteTag({ agencyId: currentUser.agency_id, tagId: tag.id })
    ).then((e) => {
      if (e.type === "tags/deleteTag/rejected") {
        setShowError(true);
      } else {
        toast(t("DELETE_SUCCESFULLY"));
      }
    });
  };

  return (
    <>
      {isLoading && <LoadingMask />}
      <AgencyTagModal
        info={showAgencyTagModal}
        onClose={() => setShowAgencyTagModal({
          show: false,
          update: false,
          tag: undefined,
          currentUser: currentUser
        })}
      />
      <AgencyDeleteTagModal
        info={showDeleteTagModal}
        onClose={() => setShowDeleteTagModal({show : false, tag: undefined})}
        deleteTag={(tag: Tag) => onDeleteSubmit(tag)}
      />
      <FilteredTable
        columns={columns}
        data={tags}
        buttonAction={
          <Button onClick={() => setShowAgencyTagModal({
              show: true,
              update: false,
              tag: undefined,
              currentUser: currentUser
            })}
          >
            <span className="flex items-center font-semibold pr-3">
              <div className="w-8 p-1 aspect-square mr-2">
                <PlusCircleIcon />
              </div>
              {t("NEW_TAG")}
            </span>
          </Button>
        }
      />
    </>
  );
};
